import { ChainID, Timeframe } from '@ankr.com/chains-list';
import { useMemo } from 'react';

import { StatsTimeframe } from 'modules/stats/types';
import { ProjectStatsParams } from 'modules/projects/types';
import { selectAllPathsByChainId } from 'modules/chains/store/selectors';
import { useAppSelector } from 'store/useAppSelector';
import { useJWTsManager } from 'domains/jwtToken/hooks/useJWTsManager';
import { usePrivateStats } from 'modules/stats/hooks/usePrivateStats';
import { useProjectsStatsParams } from 'modules/projects/hooks/useProjectsStatsParams';

import { useTimeframe } from '../ChainItemSections/hooks/useTimeframe';

export interface IUseChainProjectsProps {
  chainId: ChainID;
}

const timeframes = [Timeframe.Hour, Timeframe.Day];

export const useChainProjects = ({ chainId }: IUseChainProjectsProps) => {
  const { timeframe, timeframeTabs } = useTimeframe({
    initialTimeframe: Timeframe.Day,
    timeframes,
  });

  const isDayTimeframe = timeframe === Timeframe.Day;

  const { isLoaded, jwts, jwtsLoading, shouldShowTokenManager } =
    useJWTsManager();

  const blockchains = useAppSelector(state =>
    selectAllPathsByChainId(state, chainId),
  );

  const { params: statsParams1h } = usePrivateStats(
    useProjectsStatsParams({
      blockchains,
      timeframe: StatsTimeframe.Hour,
      timeframeShift: 0,
      skipFetching: !isLoaded || isDayTimeframe,
    }),
  );

  const { params: statsParams1d } = usePrivateStats(
    useProjectsStatsParams({
      blockchains,
      timeframe: StatsTimeframe.Day,
      timeframeShift: 0,
      skipFetching: !isLoaded || !isDayTimeframe,
    }),
  );

  const projectsStatsParams = useMemo(
    (): ProjectStatsParams => ({
      [StatsTimeframe.Hour]: statsParams1h,
      [StatsTimeframe.Day]: statsParams1d,
    }),
    [statsParams1h, statsParams1d],
  );

  return {
    jwts,
    jwtsLoading,
    projectsStatsParams,
    shouldShowTokenManager,
    timeframe,
    timeframeTabs,
  };
};
